/**
 * La question jouée par la démo guidée d'une classe : l'exercice de démo du niveau (`demo-exercises.ts`, RPC
 * anonyme `get_exercise_v2_demo`) et sa feuille du moteur v2 (`get_scratch_models_demo`, version `packs`). Rien de
 * propre à un exercice ici : changer l'exercice de démo d'une classe suffit, le coach le guide tout seul (s'il
 * peut aller jusqu'à la réponse, sinon la question se joue sans guide).
 */
import { supabase } from '@/lib/supabase';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { loadExerciseDemoV2, type DemoExerciseV2 } from '../exercise-demo-loader-v2';

export interface GuidedQuestion {
  exercise: DemoExerciseV2;
  /** la question (premier bloc `statement_question`) */
  question: ExerciseBlock | null;
  /** feuille du moteur v2 de la question ; null : la question se joue sans guide */
  model: unknown;
}

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: unknown }>;

/** La feuille `packs` publiée d'une question, dans la réponse de `get_scratch_models_demo`. PUR. */
export function packsModelFor(raw: unknown, questionId: string): unknown {
  const models = (raw as { models?: unknown } | null)?.models;
  if (!Array.isArray(models)) return null;
  const row = (models as Record<string, unknown>[]).find(
    (m) => m.track === 'packs' && m.question_block_id === questionId,
  );
  const model = row?.model;
  return model && typeof model === 'object' ? model : null;
}

/**
 * Les feuilles de la question, sans jamais lever : une erreur = pas de guide. ⚠️ `rpc` s'appelle SUR le client
 * (`supabase.rpc(…)`) : détachée dans une variable, elle perd son `this` et lève.
 */
async function loadScratch(exerciseId: string): Promise<{ data: unknown; error: unknown }> {
  try {
    return await (supabase.rpc as unknown as UntypedRpc)('get_scratch_models_demo', {
      p_exercise_id: exerciseId,
    });
  } catch {
    return { data: null, error: true };
  }
}

/** Charge la question de démo et sa feuille. null : exercice introuvable. Lève sur erreur réseau. */
export async function loadGuidedQuestion(exerciseId: string): Promise<GuidedQuestion | null> {
  const [exercise, scratch] = await Promise.all([
    loadExerciseDemoV2(exerciseId),
    // sans feuille, la question se joue quand même (sans guide)
    loadScratch(exerciseId),
  ]);
  if (!exercise) return null;
  const question = exercise.blocks.find((b) => b.block_type === 'statement_question') ?? null;
  const model = question && !scratch.error ? packsModelFor(scratch.data, question.id) : null;
  return { exercise, question, model };
}
